<template>
  <div ref="container">
    <canvas ref="canvas"  />
  </div>
</template>

<script>
import * as PIXI from 'pixi.js'
import values from 'lodash/values'
import { RAF } from '@monogrid/js-utils'
import ResizeObserver from 'resize-observer-polyfill'

export default {
  name: 'ImagePlayer',
  // mixins: [Viewport],
  props: {
    offsetX: {
      type: Number,
      required: false,
      default: 0
    },
    offsetY: {
      type: Number,
      required: false,
      default: 0
    },
    frames: {
      type: Array,
      required: true
    },
    transparencyFrames: {
      type: Array,
      required: false
    },
    fps: {
      type: Number,
      required: false,
      default: 30
    },
    currentFrame: {
      type: Number,
      required: true,
      default: 0
    },
    framesWidth: {
      type: Number,
      required: false,
      default: 2048
    },
    framesHeight: {
      type: Number,
      required: false,
      default: 1024
    },
    loading: {
      type: Boolean,
      required: true
    },
    loadProgress: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      viewPort: { width: 100, height: 100 },
      state: 'pause',
      loop: false
    }
  },

  beforeDestroy () {
    RAF.remove(this.render)
  },

  async mounted () {
    // PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR
    this.app = new PIXI.Application({
      view: this.$refs.canvas,
      autoDensity: false,
      transparent: true,
      autoStart: false,
      sharedTicker: false,
      antialias: true,
      width: this.viewPort.width,
      height: this.viewPort.height
      // forceCanvas: true
      // resizeTo: this.$refs.container
    })
    this.time = new Date().getTime()
    this.currentFrameDelta = 0

    this.sprite = new PIXI.Sprite()
    this.sprite.anchor.set(0.5)
    this.app.stage.addChild(this.sprite)

    this.spriteMask = new PIXI.Sprite()
    this.spriteMask.anchor.set(0.5)
    this.app.stage.addChild(this.spriteMask)

    this.playToEndFrame = 0

    window.__PIXI_INSPECTOR_GLOBAL_HOOK__ && window.__PIXI_INSPECTOR_GLOBAL_HOOK__.register({ PIXI: PIXI })

    await this.loadFrames()

    const ro = new ResizeObserver((entries, observer) => {
      this.viewPort = entries[0].contentRect
      // this.onResize(entries[0].contentRect)
    })
    ro.observe(this.$el)

    this.onResize()
    RAF.add(this.render)
  },

  watch: {
    async frames () {
      await this.loadFrames()
      this.onResize()
    },
    viewPort () {
      this.onResize()
    },
    state () {
      this.currentFrameDelta = 0
    },
    currentFrame () {
      this.currentFrameDelta = 0
    }
  },

  methods: {
    onResize () {
      if (!this.app || !this.sprite) return
      this.sprite.width = this.spriteMask.width = this.framesWidth
      this.sprite.height = this.spriteMask.height = this.framesHeight

      const spriteRatio = this.sprite.width / this.sprite.height
      const screenRatio = this.viewPort.width / this.viewPort.height

      if (spriteRatio > screenRatio) {
        this.sprite.height = this.spriteMask.height = this.viewPort.height
        this.sprite.width = this.spriteMask.width = this.sprite.height * spriteRatio
      } else {
        this.sprite.width = this.spriteMask.width = this.viewPort.width
        this.sprite.height = this.spriteMask.height = this.sprite.width / spriteRatio
      }

      const offsetX = this.offsetX
      const offsetY = this.offsetY
      // const offsetX = this.viewPort.width * this.offsetX
      // const offsetY = this.viewPort.height * this.offsetY

      this.sprite.position = this.spriteMask.position = new PIXI.Point(offsetX + (this.viewPort.width / 2), offsetY + (this.viewPort.height / 2))
      // this.app.resize()
      this.app.renderer.view.width = this.viewPort.width
      this.app.renderer.view.height = this.viewPort.height
      this.app.screen.width = this.viewPort.width
      this.app.screen.height = this.viewPort.height

      this.renderedOnResize = false
      this.render()
      this.renderedOnResize = true
    },
    render () {
      if (this.renderedOnResize) {
        // console.log('skipped')
        this.renderedOnResize = false
        return
      }
      const now = new Date().getTime()
      const deltaTime = (now - this.time) / 1000

      if (this.currentFrame === this.playToEndFrame && this.playToResolve) {
        this.playToResolve()
        this.playToResolve = null
        this.state = 'pause'
      }
      let cFrame = this.currentFrame

      if (this.state === 'play' && !this.loading) {
        this.currentFrameDelta += deltaTime
        const oneFrame = 1 / this.fps
        const mult = this.playToEndFrame - this.currentFrameDelta >= 0 ? 1 : -1
        let nextFrame = this.currentFrame + Math.floor((this.currentFrameDelta / oneFrame) * mult)
        // if (nextFrame > this.frames.length - 1 && this.loop) {
        //   nextFrame = 0
        // }
        nextFrame = Math.max(0, Math.min(this.frames.length - 1, nextFrame))
        // console.log(this.currentFrameDelta, nextFrame, mult)
        if (nextFrame !== this.currentFrame) {
          this.$emit('update:currentFrame', nextFrame)
          cFrame = nextFrame
          this.currentFrameDelta = 0
        }
      } else {
        this.currentFrameDelta = 0
      }

      const texture = this.textures[cFrame]
      let maskTexture
      if (this.transparencyFrames) {
        maskTexture = this.maskTextures[cFrame]
      }

      if (texture !== this.sprite.texture) {
        const prevTexture = this.textures[this.textures.indexOf(this.sprite.texture)]
        if (prevTexture && prevTexture.baseTexture) prevTexture.baseTexture.dispose()
        this.sprite.texture = texture

        if (maskTexture) {
          const prevMaskTexture = this.maskTextures[this.maskTextures.indexOf(this.spriteMask.texture)]
          if (prevMaskTexture && prevMaskTexture.baseTexture) prevMaskTexture.baseTexture.dispose()
          this.spriteMask.texture = maskTexture
        }
      }

      if (this.transparencyFrames) {
        this.sprite.mask = this.spriteMask
      } else {
        this.sprite.mask = null
      }

      this.app.render()

      this.time = now
    },
    playTo (frameNumber, loop = false) {
      return new Promise((resolve, reject) => {
        this.loop = loop
        this.state = 'play'
        this.playToEndFrame = frameNumber
        this.playToResolve = resolve
        this.currentFrameDelta = 0
      })
    },
    async loadFrames () {
      this.$emit('update:loading', true)
      this.$emit('update:loadProgress', 0)
      const resources = await this.pixiLoaderPromise(this.frames, true)
      this.textures = values(resources).map(r => r.texture)

      if (this.transparencyFrames) {
        const transparencyResources = await this.pixiLoaderPromise(this.transparencyFrames, true)
        this.maskTextures = values(transparencyResources).map(r => r.texture)
      }

      this.currentFrameDelta = 0
      this.$emit('update:loading', false)
      await this.$nextTick()
      this.$emit('loadComplete')
    },
    pixiLoaderPromise (files, fireLoadEvents = false) {
      return new Promise((resolve, reject) => {
        const loader = new PIXI.Loader()
        const cachedResources = {}
        files.forEach(frame => {
          if (PIXI.utils.TextureCache[frame]) {
            cachedResources[frame] = { texture: PIXI.utils.TextureCache[frame] }
            // cachedResources[frame].texture
          } else {
            loader.add(frame, frame)
          }
        })

        loader.onProgress.add((loader, resources) => {
          if (fireLoadEvents) this.$emit('update:loadProgress', loader.progress)
        }) // called once per loaded/errored file

        loader.onError.add((loader, resources) => {
          reject(new Error('Loading Failed'))
        }) // called once per errored file

        loader.onComplete.add((loader, resources) => {
          resolve({ ...resources, ...cachedResources })
          if (fireLoadEvents) {
            this.$emit('update:loadProgress', 100)
          }
        }) // called once when the queued resources all load.
        for (const key in loader.resources) {
          loader.resources[key].loadType = 2
        }
        loader.load()
      })
    }
  }
}
</script>
